import { memo } from 'react'
import { Theme, Typography } from '@mui/material'
import { observer } from 'mobx-react'

import useEmotionStyles from 'hooks/useEmotionStyles'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { AgeGroup } from 'constants/components'
import { isPremiumCustomer } from 'utils/user'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { getAge } from 'utils/date'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { Experiments } from 'CustomOptimizelyProvider'

interface MotivationalTextBelowProps {
  percentage: number
  label?: string
}

const LAST_STEP_TEXT = 'Letzter Schritt!'
const TWO_STEPS_LEFT_TEXT = 'Nur noch 2 Schritte'
const THREE_STEPS_LEFT_TEXT = 'Nur noch 3 Schritte'

const PAGE_WITH_CUSTOM_TEXT = {
  rdi: TWO_STEPS_LEFT_TEXT,
  activeLoans: THREE_STEPS_LEFT_TEXT,
  vehicle: THREE_STEPS_LEFT_TEXT,
  payoutWithoutDac: TWO_STEPS_LEFT_TEXT,
  dacSmavaHard: LAST_STEP_TEXT,
  verifiedPin: LAST_STEP_TEXT
}

const getMessage = (
  percentage: number,
  currentPageInSession: string | null,
  isCarLoanPurpose: boolean,
  label?: string,
  overrideCustomText?: string | undefined
): string => {
  if (overrideCustomText) {
    return overrideCustomText
  }

  if (
    currentPageInSession === 'payoutWithoutDac' ||
    currentPageInSession === 'rdi' ||
    currentPageInSession === 'dacSmavaHard' ||
    (currentPageInSession === 'activeLoans' && !isCarLoanPurpose) ||
    (currentPageInSession === 'vehicle' && isCarLoanPurpose) ||
    currentPageInSession === 'verifiedPin'
  ) {
    return PAGE_WITH_CUSTOM_TEXT[currentPageInSession]
  }

  if (percentage) {
    return `${percentage} % ${label || 'geschafft'}`
  }
  return ''
}

const MotivationalTextBelow = observer(
  ({ percentage, label }: MotivationalTextBelowProps) => {
    const { motivationalTextBelow } = useEmotionStyles(styles)

    const {
      rootStore: {
        experimentData,
        navigation: { currentPageInSession },
        traversal: {
          data: {
            common: { purpose, amount },
            debtors: {
              primary: {
                personal: { birthday },
                income
              }
            }
          }
        }
      }
    } = useRootStore()

    const ageGroup = useAgeGroup()
    const debtorsAge = getAge(birthday)
    const isCustomerPremium = isPremiumCustomer({
      debtorsAge,
      amount,
      income
    })

    const isNotMiddleAgeNonPremium = !(
      ageGroup === AgeGroup.MIDDLE_AGE_GROUP && !isCustomerPremium
    )

    const isUserInExperiment =
      isNotMiddleAgeNonPremium &&
      // @ts-ignore - experimentData type needs refinement for dynamic access
      (experimentData[Experiments.CAT_4291_DAC_SKIP_BUTTON_V3] ===
        PossibleVariants.VARIATION_B ||
        // @ts-ignore - experimentData type needs refinement for dynamic access
        experimentData[Experiments.CAT_4291_DAC_SKIP_BUTTON_V3] ===
          PossibleVariants.VARIATION_C)

    const getSmavaMiddleAgeCustomText = (
      isInMiddleOrYoungAgeGroup: boolean,
      currentPage: string | null,
      currentPercent: number,
      skipDacForPremiumCustomers: boolean
    ): string | undefined => {
      if (currentPageInSession === 'verifiedPin') {
        return PAGE_WITH_CUSTOM_TEXT[currentPageInSession]
      }
      if (
        (skipDacForPremiumCustomers &&
          currentPage === 'payoutAccount' &&
          !isUserInExperiment) ||
        (isInMiddleOrYoungAgeGroup && currentPage === 'dac')
      ) {
        return LAST_STEP_TEXT
      }
      if (isInMiddleOrYoungAgeGroup && currentPage === 'payoutAccount') {
        return TWO_STEPS_LEFT_TEXT
      }
      if (isInMiddleOrYoungAgeGroup && currentPercent) {
        return `${currentPercent} % geschafft`
      }
      return undefined
    }

    const skipDacForPremiumCustomers = isPremiumCustomer({
      debtorsAge,
      amount,
      income
    })

    const overrideCustomText = getSmavaMiddleAgeCustomText(
      ageGroup === AgeGroup.MIDDLE_AGE_GROUP ||
        ageGroup === AgeGroup.YOUNG_AGE_GROUP,
      currentPageInSession,
      percentage,
      skipDacForPremiumCustomers
    )

    const isCarLoanPurpose =
      purpose === 'PRE_OWNED_CAR' || purpose === 'NEW_CAR'

    const message = getMessage(
      percentage,
      currentPageInSession,
      isCarLoanPurpose,
      label,
      overrideCustomText
    )

    if (!message) {
      return null
    }

    return (
      <Typography variant="caption" className={motivationalTextBelow}>
        {message}
      </Typography>
    )
  }
)

export default memo(MotivationalTextBelow)

const styles = (theme: Theme) => ({
  motivationalTextBelow: {
    color: theme.palette.grey[800]
  }
})
